// AIT-529 Task 7 — affiliate invite consent handoff storage.
//
// The invite consent screen (/invite/:slug) records consent
// (POST /invite/:slug/consent -> { consentId }) BEFORE the client signs in
// via WorkOS. The consentId must survive the WorkOS round-trip (a full page
// navigation away and back) so the grant (POST /invite/complete) can finish once the
// client is authenticated. sessionStorage is tab-scoped (same rationale as
// handoff-storage.ts:20) -- bound to this browser tab's lifecycle, cleared
// when the tab closes, never leaks across tabs.
//
// Cleared ONLY after a successful or terminal completion response (never on
// mere page unload/mount) so a refresh on the picker can still recover
// the pending consent.

const INVITE_CONTEXT_STORAGE_KEY = 'hookmyapp_invite_v1';

export interface InviteContext {
  slug: string;
  consentId: string;
}

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function isValidInviteContext(v: unknown): v is InviteContext {
  if (!v || typeof v !== 'object') return false;
  const c = v as Record<string, unknown>;
  return typeof c.slug === 'string' && typeof c.consentId === 'string' && /^agc_/.test(c.consentId);
}

/** Persist the invite context. No-op when sessionStorage is unavailable. */
export function setInviteContext(context: InviteContext): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(INVITE_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Quota / privacy-mode failures: never block the Continue flow.
  }
}

/**
 * Read-only: returns the stored context (or null) without clearing it.
 * Validates shape -- a corrupted/foreign value returns null rather than
 * throwing.
 */
export function peekInviteContext(): InviteContext | null {
  if (!hasSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(INVITE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidInviteContext(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Clear the stored context. Call after a successful or terminal completion. */
export function clearInviteContext(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(INVITE_CONTEXT_STORAGE_KEY);
  } catch {
    // Ignore -- worst case the next grant attempt retries with stale data.
  }
}

/** Exposed so tests can clear state between cases without hard-coding the string. */
export const INVITE_CONTEXT_STORAGE_KEY_FOR_TESTS = INVITE_CONTEXT_STORAGE_KEY;
